import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  User,
  CheckCircle,
  Trophy,
  Users,
  Zap,
  TrendingUp,
  Phone,
  Calendar,
  Upload,
  Image as ImageIcon,
  Loader2
} from "lucide-react";

const POSITIONS = ["Levantador", "Ponteiro", "Oposto", "Central", "Líbero"];
const YANKEE_OPTIONS = ["C-", "C", "C+", "B-", "B", "B+", "A-", "A", "A+"];

export default function PlayerRegisterPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [gameday, setGameday] = useState(null);
  const [playerTeam, setPlayerTeam] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    position: "",
    position2: "",
    yankee: "",
    photo_url: "",
    email: "",
    phone: "",
    birth_date: ""
  });

  useEffect(() => {
    fetchInvite();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchInvite = async () => {
    try {
      const res = await axios.get(`${API}/player-invites/${token}`);
      setInvite(res.data.invite);
      setGameday(res.data.gameday);
      setPlayerTeam(res.data.player_team);
      
      // Pre-fill email if available
      if (res.data.invite?.email) {
        setFormData(prev => ({ ...prev, email: res.data.invite.email }));
      }
      
      // Check if already registered
      if (res.data.invite?.status === 'registered') {
        setIsRegistered(true);
      }
    } catch (error) {
      toast.error("Convite não encontrado ou expirado");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Use apenas imagens JPG, PNG, GIF ou WEBP");
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 10MB");
      return;
    }

    setIsUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      // Use Cloudinary endpoint for persistent storage
      const res = await axios.post(`${API}/upload/photo/cloudinary`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Store the Cloudinary URL
      setFormData(prev => ({ ...prev, photo_url: res.data.photo_url }));
      toast.success("Foto salva na nuvem!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar foto");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.last_name || !formData.position || 
        !formData.position2 || !formData.yankee) {
      toast.error("Preencha todos os campos obrigatórios");
      return;
    }

    if (!formData.phone) {
      toast.error("Informe seu telefone");
      return;
    }

    if (!formData.birth_date) {
      toast.error("Informe sua data de nascimento");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/player-invites/${token}/register`, {
        token,
        ...formData
      });
      toast.success("Cadastro realizado com sucesso!");
      setIsRegistered(true);
      fetchInvite(); // Refresh to get team info
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao realizar cadastro");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPhone = (value) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Aplica a máscara (XXX) XXX-XXXX (formato EUA)
    if (numbers.length <= 3) {
      return `(${numbers}`;
    } else if (numbers.length <= 6) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    } else if (numbers.length <= 10) {
      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    }
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData({ ...formData, phone: formatted });
  };

  const getTeamColor = (teamNumber) => {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500'];
    return colors[teamNumber - 1] || 'bg-gray-500';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Carregando...
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="h-16 w-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <User className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-xl font-bold mb-2">Convite Não Encontrado</h2>
            <p className="text-muted-foreground">
              Este link de convite é inválido ou já expirou.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already registered - show team info
  if (isRegistered) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="container max-w-2xl mx-auto py-8">
          {/* Success Header */}
          <Card className="bg-green-500/10 border-green-500/50 mb-6">
            <CardContent className="pt-6 text-center">
              <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h2 className="text-2xl font-bold text-green-500 mb-2">Cadastro Confirmado!</h2>
              <p className="text-muted-foreground">
                Bem-vindo ao VolleyxBrasil! Seu cadastro foi realizado com sucesso.
              </p>
            </CardContent>
          </Card>

          {/* Team Assignment */}
          {playerTeam && gameday && (
            <Card className="bg-card border-border/50 shadow-xl">
              <CardHeader className="text-center">
                <CardTitle className="text-xl font-bold flex items-center justify-center gap-2">
                  <Trophy className="h-6 w-6 text-yellow-400" />
                  Seu Time - {gameday.date}
                </CardTitle>
                <CardDescription>
                  Você foi escalado para jogar!
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`${getTeamColor(playerTeam.team_number)} rounded-xl p-6 text-white text-center`}>
                  <h3 className="text-3xl font-black mb-2">Time {playerTeam.team_number}</h3>
                  
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="bg-black/20 rounded-lg p-3">
                      <Zap className="h-5 w-5 mx-auto mb-1" />
                      <p className="text-xs opacity-80">Power</p>
                      <p className="text-xl font-bold">{playerTeam.power?.toFixed(1)}</p>
                    </div>
                    <div className="bg-black/20 rounded-lg p-3">
                      <TrendingUp className="h-5 w-5 mx-auto mb-1" />
                      <p className="text-xs opacity-80">Média</p>
                      <p className="text-xl font-bold">{playerTeam.average_score?.toFixed(1)}</p>
                    </div>
                    <div className="bg-black/20 rounded-lg p-3">
                      <Trophy className="h-5 w-5 mx-auto mb-1" />
                      <p className="text-xs opacity-80">Prob. Vitória</p>
                      <p className="text-xl font-bold">{playerTeam.win_probability?.toFixed(1)}%</p>
                    </div>
                  </div>

                  {playerTeam.teammates && playerTeam.teammates.length > 0 && (
                    <div className="mt-6">
                      <p className="text-sm opacity-80 mb-2">
                        <Users className="h-4 w-4 inline mr-1" />
                        Seus companheiros de time:
                      </p>
                      <div className="flex flex-wrap justify-center gap-2">
                        {playerTeam.teammates.map((name) => (
                          <Badge key={name} variant="secondary" className="bg-white/20">
                            {name}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {!playerTeam && (
            <Card className="bg-card border-border/50">
              <CardContent className="pt-6 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Você ainda não foi escalado para nenhum jogo. Aguarde a definição dos times!
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  // Registration Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 p-4">
      <div className="container max-w-2xl mx-auto py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="VolleyxBrasil" className="w-24 h-24 mx-auto mb-4" />
          <h1 className="text-3xl font-black tracking-tighter text-primary mb-2">VolleyxBrasil</h1>
          <p className="text-muted-foreground">Complete seu cadastro para participar</p>
        </div>

        {/* Registration Form */}
        <Card className="bg-card/80 backdrop-blur-sm border-border/50 shadow-xl">
          <CardHeader>
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <User className="h-5 w-5 text-primary" />
              Cadastro de Jogador
            </CardTitle>
            <CardDescription>
              Preencha seus dados para completar o cadastro
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Nome */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Nome *</Label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    placeholder="Seu nome"
                    className="bg-input/50"
                    required
                    data-testid="first-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Sobrenome *</Label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    placeholder="Seu sobrenome"
                    className="bg-input/50"
                    required
                    data-testid="last-name-input"
                  />
                </div>
              </div>

              {/* Telefone e Data de Nascimento */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    Telefone *
                  </Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder="(555) 123-4567"
                    className="bg-input/50"
                    required
                    data-testid="phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    Data de Nascimento *
                  </Label>
                  <Input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    className="bg-input/50"
                    required
                    data-testid="birth-date-input"
                  />
                </div>
              </div>

              {/* Posições */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Posição Principal *</Label>
                  <Select value={formData.position} onValueChange={(v) => setFormData({ ...formData, position: v })}>
                    <SelectTrigger className="bg-input/50" data-testid="position-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {POSITIONS.map(pos => (
                        <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">2ª Posição *</Label>
                  <Select value={formData.position2} onValueChange={(v) => setFormData({ ...formData, position2: v })}>
                    <SelectTrigger className="bg-input/50" data-testid="position2-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {POSITIONS.filter(p => p !== formData.position).map(pos => (
                        <SelectItem key={pos} value={pos}>{pos}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Nível e Email */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Nível *</Label>
                  <Select value={formData.yankee} onValueChange={(v) => setFormData({ ...formData, yankee: v })}>
                    <SelectTrigger className="bg-input/50" data-testid="yankee-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {YANKEE_OPTIONS.map(level => (
                        <SelectItem key={level} value={level}>{level}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Email (opcional)</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="seu@email.com"
                    className="bg-input/50"
                    data-testid="email-input"
                  />
                </div>
              </div>

              {/* Foto Upload */}
              <div className="space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <ImageIcon className="h-4 w-4 text-muted-foreground" />
                  Foto (opcional)
                </Label>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <label className="cursor-pointer">
                      <div className="flex items-center justify-center gap-2 h-12 px-4 bg-secondary/50 hover:bg-secondary/70 rounded-lg border border-dashed border-border transition-colors">
                        {isUploading ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-sm">Enviando...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            <span className="text-sm">
                              {formData.photo_url ? "Trocar foto" : "Enviar foto"}
                            </span>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handlePhotoUpload}
                        className="hidden"
                        disabled={isUploading}
                        data-testid="photo-upload-input"
                      />
                    </label>
                  </div>
                  
                  {formData.photo_url && (
                    <div className="flex-shrink-0">
                      <img 
                        src={formData.photo_url} 
                        alt="Preview" 
                        className="w-16 h-16 rounded-full object-cover border-2 border-primary shadow-lg"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Formatos aceitos: JPG, PNG, GIF, WEBP (máx 10MB)
                </p>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-primary text-primary-foreground font-bold h-12 text-base shadow-lg shadow-primary/25"
                disabled={isSubmitting}
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                    Cadastrando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Completar Cadastro
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
